/**
 * DSS nutrient report — tabbed professional layout (not a long scaffold scroll).
 * One Result[] drives Balance, Ratios, Sources, EC.
 */
import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Tabs,
  Tab,
  Stack,
  Divider,
} from '@mui/material';
import Grid from '../common/Grid';
import {
  buildReportResultSource,
  resultPpm,
  resultTotalN,
  nutrientStatus,
  type ReportResultSource,
} from '../../utils/ReportResultSource';
import { verifyRecipeCalculation } from '../../utils/recipeCalculationVerification';
import CalculationVerificationPanel from './CalculationVerificationPanel';

export interface NutrientReportData {
  metadata: {
    recipeName: string;
    cropType?: string;
    growthStage: string;
    targetEC: number;
    targetPH: number;
    waterSource: string;
    createdDate: string;
    targetVolume: number;
    concentrationUnit: string;
    massUnit: string;
    stockVolumePerTank?: number;
    workingVolumeTarget?: number;
    dilutionRatio?: number;
    modelVersion?: string;
    recipeId?: string;
    batchId?: string;
    userId?: string;
    calculationEngine?: string;
    complianceStandard?: string;
  };
  formulation: {
    name: string;
    elements: Array<{ symbol: string; concentration: number }>;
  };
  waterProfile: {
    name: string;
    elements: Array<{ symbol: string; concentration: number }>;
  };
  saltAmounts: Array<{
    id: string;
    name: string;
    formula: string;
    amount: number;
    unit: string;
    purity?: number;
    elements: Array<{ symbol: string; percentage: number }>;
    tankAssignment?: string;
  }>;
  achievedConcentrations: Array<{
    symbol: string;
    achieved: number;
    target: number;
    difference: number;
  }>;
  solutionProperties: {
    electricalConductivity: number;
    finalPH: number;
    totalDissolvedSolids: number;
    ionicStrength?: number;
  };
  phAdjustment?: {
    agent: string;
    volume: number;
    finalPH: number;
  };
}

interface ProfessionalNutrientReportProps {
  data: NutrientReportData;
  /** When true, hide outer chrome (Results page already has header) */
  embedded?: boolean;
}

type TabKey =
  | 'summary'
  | 'balance'
  | 'ratios'
  | 'salts'
  | 'sources'
  | 'verify'
  | 'prep';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'summary', label: 'Summary' },
  { key: 'balance', label: 'Balance' },
  { key: 'ratios', label: 'Ratios' },
  { key: 'salts', label: 'Salts & tanks' },
  { key: 'sources', label: 'Sources' },
  { key: 'verify', label: 'Verify' },
  { key: 'prep', label: 'Prep' },
];

function formatElementSymbol(symbol: string): string {
  const map: Record<string, string> = {
    N: 'N (total)',
    N_NO3: 'N-NO₃',
    N_NH4: 'N-NH₄',
    P: 'P',
    K: 'K',
    Ca: 'Ca',
    Mg: 'Mg',
    S: 'S',
    Fe: 'Fe',
    Mn: 'Mn',
    Zn: 'Zn',
    Cu: 'Cu',
    B: 'B',
    Mo: 'Mo',
    Na: 'Na',
    Si: 'Si',
    Cl: 'Cl',
  };
  return map[symbol] || symbol;
}

function TabPanel({
  value,
  active,
  children,
}: {
  value: TabKey;
  active: TabKey;
  children: React.ReactNode;
}) {
  if (value !== active) return null;
  return (
    <Box role="tabpanel" sx={{ pt: 2 }}>
      {children}
    </Box>
  );
}

function MetaRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', gap: 1, py: 0.35 }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 88 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {value}
      </Typography>
    </Box>
  );
}

const ProfessionalNutrientReport: React.FC<ProfessionalNutrientReportProps> = ({
  data,
  embedded = false,
}) => {
  const [tab, setTab] = useState<TabKey>('summary');

  const volumeL = useMemo(
    () =>
      Number(data.metadata?.stockVolumePerTank) ||
      Number(data.metadata?.targetVolume) ||
      1,
    [data.metadata]
  );
  const cf = useMemo(
    () => Math.max(1, Number(data.metadata?.dilutionRatio) || 1),
    [data.metadata]
  );
  const unit = data.metadata.concentrationUnit || 'mg/L';
  const massUnit = data.metadata.massUnit || 'g';
  const mlPerL = Math.round(1000 / cf);

  const result: ReportResultSource = useMemo(
    () =>
      buildReportResultSource({
        formulationElements: data.formulation?.elements || [],
        waterElements: data.waterProfile?.elements || [],
        saltAmounts: data.saltAmounts || [],
        volumeL,
        concentrationFactor: cf,
        stageAchieved: data.achievedConcentrations || [],
        stageEC: data.solutionProperties?.electricalConductivity,
        stageIonicStrength: data.solutionProperties?.ionicStrength,
      }),
    [data, volumeL, cf]
  );

  const calculationVerification = useMemo(() => {
    const workingVolumeL =
      Number(data.metadata?.workingVolumeTarget) || volumeL * cf || 1000;
    return verifyRecipeCalculation({
      salts: (data.saltAmounts || []).map((s) => ({
        id: s.id,
        name: s.name,
        amount: Number(s.amount) || 0,
        purity: s.purity,
        elements: s.elements,
        tankAssignment: s.tankAssignment || 'A',
      })),
      workingVolumeL,
      stockVolumeAL: volumeL,
      stockVolumeBL: volumeL,
      injectionRatioA: cf,
      injectionRatioB: cf,
      waterElements: data.waterProfile?.elements || [],
      reportedConcentrations: result.elements.map((e) => ({
        symbol: e.symbol,
        achieved: e.achieved,
      })),
    });
  }, [data, volumeL, cf, result.elements]);

  const tankA = data.saltAmounts.filter((s) => (s.tankAssignment || 'A') === 'A');
  const tankB = data.saltAmounts.filter((s) => s.tankAssignment === 'B');
  const hasAB = tankB.length > 0;

  const ratios = useMemo(() => {
    const { total: N, no3: N_NO3, nh4: N_NH4 } = resultTotalN(result);
    const K = resultPpm(result, 'K');
    const Ca = resultPpm(result, 'Ca');
    const Mg = resultPpm(result, 'Mg');

    const pair = (a: number, b: number) => {
      if (a <= 0 || b <= 0) return { text: '—', ok: false as boolean | null };
      return { text: `1:${(b / a).toFixed(2)}`, ok: null as boolean | null };
    };

    const nk = pair(N, K);
    if (N > 0 && K > 0) {
      const r = K / N;
      nk.ok = r >= 1.2 && r <= 1.8;
    }
    const camg = pair(Ca, Mg);
    if (Ca > 0 && Mg > 0) {
      const r = Mg / Ca;
      camg.ok = r >= 0.2 && r <= 0.4;
    }
    const kca = pair(K, Ca);
    if (K > 0 && Ca > 0) {
      const r = Ca / K;
      kca.ok = r >= 0.6 && r <= 1.0;
    }

    let no3nh4 = { text: '—', ok: null as boolean | null, note: '' };
    if (N_NO3 > 0 && N_NH4 <= 0) {
      no3nh4 = { text: '1:0', ok: true, note: 'No NH₄-N' };
    } else if (N_NO3 > 0 && N_NH4 > 0) {
      const r = N_NH4 / N_NO3;
      no3nh4 = { text: `1:${r.toFixed(2)}`, ok: r <= 0.25, note: '' };
    }

    return [
      { name: 'N:K', ...nk, ppm: `N ${N.toFixed(1)} · K ${K.toFixed(1)}` },
      { name: 'Ca:Mg', ...camg, ppm: `Ca ${Ca.toFixed(1)} · Mg ${Mg.toFixed(1)}` },
      { name: 'K:Ca', ...kca, ppm: `K ${K.toFixed(1)} · Ca ${Ca.toFixed(1)}` },
      {
        name: 'NO₃-N:NH₄-N',
        ...no3nh4,
        ppm: `NO₃ ${N_NO3.toFixed(1)} · NH₄ ${N_NH4.toFixed(1)}`,
      },
    ];
  }, [result]);

  const achievedPH = Number(
    data.phAdjustment?.finalPH ?? data.solutionProperties?.finalPH ?? 0
  );

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      {/* Compact identity strip */}
      {!embedded && (
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {data.metadata.recipeName || data.formulation.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {data.waterProfile.name} · CF 1:{cf} · {volumeL} {massUnit === 'g' ? 'L stock' : 'stock'} ·{' '}
            {new Date(data.metadata.createdDate).toLocaleDateString()}
          </Typography>
        </Box>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="Recipe report sections"
          sx={{
            minHeight: 42,
            '& .MuiTab-root': { minHeight: 42, py: 1, textTransform: 'none', fontWeight: 600 },
          }}
        >
          {TABS.map((t) => (
            <Tab key={t.key} value={t.key} label={t.label} />
          ))}
        </Tabs>
      </Box>

      <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 2 }}>
        {/* —— SUMMARY —— */}
        <TabPanel value="summary" active={tab}>
          <Grid container spacing={2}>
            <Grid xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                Context
              </Typography>
              <MetaRow label="Crop" value={data.formulation.name} />
              <MetaRow label="Water" value={data.waterProfile.name} />
              <MetaRow label="Stage" value={data.metadata.growthStage || '—'} />
              <MetaRow
                label="Date"
                value={new Date(data.metadata.createdDate).toLocaleDateString()}
              />
            </Grid>
            <Grid xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                Stock
              </Typography>
              <MetaRow label="Volume" value={`${volumeL} L / tank`} />
              <MetaRow label="CF" value={`1:${cf} (${mlPerL} mL/L final)`} />
              <MetaRow
                label="Working"
                value={`${data.metadata.workingVolumeTarget || volumeL * cf} L`}
              />
              <MetaRow label="Tanks" value={hasAB ? 'A + B' : 'Single'} />
            </Grid>
            <Grid xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                Working solution
              </Typography>
              <MetaRow
                label="EC"
                value={`${result.electricalConductivity.toFixed(2)} dS/m (${result.ecSource})`}
              />
              <MetaRow
                label="pH"
                value={achievedPH > 0 ? achievedPH.toFixed(1) : '—'}
              />
              <MetaRow
                label="TDS"
                value={
                  data.solutionProperties?.totalDissolvedSolids
                    ? `${Number(data.solutionProperties.totalDissolvedSolids).toFixed(1)} g`
                    : '—'
                }
              />
              <MetaRow label="Salts" value={`${data.saltAmounts.length} products`} />
            </Grid>
          </Grid>

          {result.warnings.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {result.warnings.map((w, i) => (
                <Typography key={i} variant="body2">
                  {w}
                </Typography>
              ))}
            </Alert>
          )}

          <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 1 }}>
            Salt amounts (stock)
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Salt</TableCell>
                  <TableCell>Formula</TableCell>
                  <TableCell align="right">Amount ({massUnit})</TableCell>
                  <TableCell align="right">g/L stock</TableCell>
                  <TableCell align="center">Tank</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.saltAmounts.map((salt) => (
                  <TableRow key={salt.id}>
                    <TableCell>{salt.name}</TableCell>
                    <TableCell>
                      <Typography variant="body2" component="code" sx={{ fontSize: 12 }}>
                        {salt.formula || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{(salt.amount || 0).toFixed(2)}</strong>
                    </TableCell>
                    <TableCell align="right">
                      {((salt.amount || 0) / volumeL).toFixed(2)}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={salt.tankAssignment || 'A'}
                        color={salt.tankAssignment === 'B' ? 'secondary' : 'primary'}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* —— BALANCE —— */}
        <TabPanel value="balance" active={tab}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Working ppm from one Result[]: Final = water + salts.
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Element</TableCell>
                  <TableCell align="right">Target ({unit})</TableCell>
                  <TableCell align="right">Water</TableCell>
                  <TableCell align="right">Final</TableCell>
                  <TableCell align="right">Δ %</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.elements.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        No balance data — re-run Recipe solve.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  result.elements.map((el) => {
                    const status = nutrientStatus(el.achieved, el.target);
                    const pct =
                      el.target > 0
                        ? ((el.achieved - el.target) / el.target) * 100
                        : 0;
                    return (
                      <TableRow key={el.symbol}>
                        <TableCell>{formatElementSymbol(el.symbol)}</TableCell>
                        <TableCell align="right">
                          {el.target > 0 ? el.target.toFixed(1) : '—'}
                        </TableCell>
                        <TableCell align="right">{el.water.toFixed(1)}</TableCell>
                        <TableCell align="right">
                          <strong>{el.achieved.toFixed(1)}</strong>
                        </TableCell>
                        <TableCell align="right">
                          {el.target > 0
                            ? `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`
                            : '—'}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={
                              status.status === 'OK'
                                ? 'OK'
                                : status.status === 'Over'
                                  ? 'Over'
                                  : status.status === 'Info'
                                    ? '—'
                                    : 'Low'
                            }
                            color={status.color}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>
                    <strong>EC (dS/m)</strong>
                  </TableCell>
                  <TableCell align="right">
                    {Number(data.metadata.targetEC) > 0
                      ? Number(data.metadata.targetEC).toFixed(2)
                      : '—'}
                  </TableCell>
                  <TableCell align="right">—</TableCell>
                  <TableCell align="right">
                    <strong>{result.electricalConductivity.toFixed(2)}</strong>
                  </TableCell>
                  <TableCell align="right">—</TableCell>
                  <TableCell align="center">
                    <Chip size="small" label={result.ecSource} variant="outlined" />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* —— RATIOS —— */}
        <TabPanel value="ratios" active={tab}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            From working Final ppm (Result[]). N = NO₃-N + NH₄-N.
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Ratio</TableCell>
                  <TableCell align="right">Value</TableCell>
                  <TableCell align="center">Band</TableCell>
                  <TableCell>ppm basis</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ratios.map((r) => (
                  <TableRow key={r.name}>
                    <TableCell>
                      <strong>{r.name}</strong>
                    </TableCell>
                    <TableCell align="right">{r.text}</TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={
                          r.ok === true
                            ? 'In band'
                            : r.ok === false
                              ? 'Check'
                              : '—'
                        }
                        color={
                          r.ok === true
                            ? 'success'
                            : r.ok === false
                              ? 'warning'
                              : 'default'
                        }
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {r.ppm}
                        {(r as any).note ? ` · ${(r as any).note}` : ''}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* —— SALTS & TANKS —— */}
        <TabPanel value="salts" active={tab}>
          <Grid container spacing={2}>
            <Grid xs={12} md={hasAB ? 6 : 12}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Tank A
                </Typography>
                {tankA.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No salts assigned.
                  </Typography>
                ) : (
                  tankA.map((s) => (
                    <Typography key={s.id} variant="body2" sx={{ py: 0.25 }}>
                      {s.name}: <strong>{(s.amount || 0).toFixed(2)} {massUnit}</strong>
                    </Typography>
                  ))
                )}
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  Total {(tankA.reduce((a, s) => a + (s.amount || 0), 0)).toFixed(1)} {massUnit} ·{' '}
                  {(
                    tankA.reduce((a, s) => a + (s.amount || 0), 0) / volumeL
                  ).toFixed(1)}{' '}
                  g/L stock
                </Typography>
              </Paper>
            </Grid>
            {hasAB && (
              <Grid xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                  <Typography variant="subtitle2" color="secondary" gutterBottom>
                    Tank B
                  </Typography>
                  {tankB.map((s) => (
                    <Typography key={s.id} variant="body2" sx={{ py: 0.25 }}>
                      {s.name}: <strong>{(s.amount || 0).toFixed(2)} {massUnit}</strong>
                    </Typography>
                  ))}
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    Total {(tankB.reduce((a, s) => a + (s.amount || 0), 0)).toFixed(1)} {massUnit} ·{' '}
                    {(
                      tankB.reduce((a, s) => a + (s.amount || 0), 0) / volumeL
                    ).toFixed(1)}{' '}
                    g/L stock
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>

          <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 1 }}>
            Full salt table
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Salt</TableCell>
                  <TableCell>Elements</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="center">Tank</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.saltAmounts.map((salt) => {
                  const els = (salt.elements || [])
                    .map((e: any) => e.symbol || e.element?.symbol)
                    .filter(Boolean)
                    .join(', ');
                  return (
                    <TableRow key={salt.id}>
                      <TableCell>
                        {salt.name}
                        <Typography variant="caption" display="block" color="text.secondary">
                          {salt.formula}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{els || '—'}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        {(salt.amount || 0).toFixed(2)} {massUnit}
                      </TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={salt.tankAssignment || 'A'} variant="outlined" />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* —— SOURCES —— */}
        <TabPanel value="sources" active={tab}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Working ppm contributed by each salt × element (Result[]).
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Salt</TableCell>
                  <TableCell>Element</TableCell>
                  <TableCell align="right">ppm</TableCell>
                  <TableCell align="center">Tank</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.saltContributions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary">
                        No contribution rows.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  result.saltContributions.map((row, i) => (
                    <TableRow key={`${row.saltId}-${row.symbol}-${i}`}>
                      <TableCell>{row.saltName}</TableCell>
                      <TableCell>{formatElementSymbol(row.symbol)}</TableCell>
                      <TableCell align="right">
                        {row.contributionPpm.toFixed(1)}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={row.tankAssignment || 'A'}
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* —— VERIFY —— */}
        <TabPanel value="verify" active={tab}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Independent math/chemistry gates — not agronomic crop fit.
          </Typography>
          <CalculationVerificationPanel verification={calculationVerification} />
        </TabPanel>

        {/* —— PREP —— */}
        <TabPanel value="prep" active={tab}>
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Dilution
              </Typography>
              <Typography variant="body2">
                CF <strong>1:{cf}</strong> → use <strong>{mlPerL} mL</strong> of each stock tank per{' '}
                <strong>1 L</strong> final (or proportional for larger tanks).
              </Typography>
              {hasAB ? (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Per 100 L final: {mlPerL * 100} mL Tank A + {mlPerL * 100} mL Tank B + water to
                  volume. Add A, mix, then B. Never mix concentrates A and B undiluted.
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Per 100 L final: {mlPerL * 100} mL stock + water to volume.
                </Typography>
              )}
            </Paper>

            {data.phAdjustment && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  pH adjustment
                </Typography>
                <Typography variant="body2">
                  {data.phAdjustment.agent}:{' '}
                  <strong>{Number(data.phAdjustment.volume).toFixed(2)} mL</strong> (as calculated)
                  · target pH {Number(data.phAdjustment.finalPH).toFixed(1)}. Adjust after dilution
                  to working strength.
                </Typography>
              </Paper>
            )}

            {hasAB && (
              <Alert severity="warning" variant="outlined">
                Keep Ca-rich and P/S-rich salts in separate stocks until final dilution to limit
                precipitation risk.
              </Alert>
            )}

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Trace
              </Typography>
              <MetaRow
                label="Engine"
                value={data.metadata.calculationEngine || 'HydroBuddy matrix'}
              />
              <MetaRow
                label="Model"
                value={data.metadata.modelVersion || 'FlahaFast DSS'}
              />
              {data.metadata.recipeId && (
                <MetaRow label="Recipe ID" value={data.metadata.recipeId} />
              )}
            </Paper>
          </Stack>
        </TabPanel>
      </Box>
    </Paper>
  );
};

export default ProfessionalNutrientReport;
